import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';

/** The maximum length of a description, for reactive forms as for signal forms */
export const DESCRIPTION_MAX_LENGTH = 300;

/** A date in the 'YYYY-MM-DD' format, for reactive forms as for signal forms */
export const ISO_FULL_DATE_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])$/;

export class WelmaValidators {
  static descriptionNotTooLong(control: AbstractControl): ValidationErrors | null {
    return Validators.maxLength(DESCRIPTION_MAX_LENGTH)(control);
  }

  /**
  A validator that requires the control's value to be a date in the 'YYYY-MM-DD' format
  */
  static isoFullDate(control: AbstractControl): ValidationErrors | null {
    const validationErrors = Validators.pattern(ISO_FULL_DATE_PATTERN)(control);
    if (validationErrors === null) {
      return null;
    }
    return { isoFullDate: true };
  }

  /**
   A validator for a school year name : should be XXXX - YYYY with YYYY = XXXX + 1
   */
  static schoolYearName(control: AbstractControl): ValidationErrors | null {
    return isValidSchoolYearName(control.value) ? null : { nameIsInvalid: true };
  }

  static calendarDate(control: AbstractControl): ValidationErrors | null {
    const value = control.value as string;
    if (!value) {
      return null;
    }

    return isValidCalendarDate(value) ? null : { dateInvalid: true };
  }

  static endDateAfterStartDate(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('startDate')?.value as string | undefined;
    const endDate = control.get('endDate')?.value as string | undefined;

    if (!startDate || !endDate) {
      return null;
    }

    return isValidDateRange(startDate, endDate) ? null : { dateRangeInvalid: true };
  }
}

/** Whether the value is an existing calendar date in the 'YYYY-MM-DD' format */
export function isValidCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return false;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);

  return (
    year > 0 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 Whether the end date is after the start date.
 Empty or invalid dates are left to the validators of each date.
 */
export function isValidDateRange(startDate: string, endDate: string): boolean {
  if (!isValidCalendarDate(startDate) || !isValidCalendarDate(endDate)) {
    return true;
  }
  return endDate > startDate;
}

/** Whether the value is a school year name : XXXX - YYYY with YYYY = XXXX + 1 */
export function isValidSchoolYearName(value: string): boolean {
  const match = /^(\d{4}) - (\d{4})$/.exec(value);
  return !!match && Number(match[2]) === Number(match[1]) + 1;
}
